import type { AgUiEvent } from './types';

export type ConsumeAgUiStreamResult = {
  terminalType: 'finished' | 'error' | null;
};

function parseSseEvent(rawEvent: string): { eventName: string; data: unknown } {
  const lines = rawEvent.split('\n');
  let eventName = '';
  const dataLines: string[] = [];

  for (const line of lines) {
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim();
      continue;
    }
    if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trimStart());
    }
  }

  const rawData = dataLines.join('\n').trim();
  let data: unknown = null;
  try {
    data = rawData ? JSON.parse(rawData) : null;
  } catch {
    data = rawData;
  }

  return { eventName, data };
}

export async function consumeAgUiStream<TState = Record<string, unknown>>(args: {
  stream: ReadableStream<Uint8Array>;
  onEvent: (event: AgUiEvent<TState>) => void;
}): Promise<ConsumeAgUiStreamResult> {
  const reader = args.stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let terminalType: 'finished' | 'error' | null = null;

  try {
    while (true) {
      let value: Uint8Array | undefined;
      let done = false;

      try {
        const readResult = await reader.read();
        value = readResult.value;
        done = readResult.done;
      } catch (error) {
        if ((error as DOMException)?.name === 'AbortError') {
          return { terminalType };
        }
        throw error;
      }

      if (value?.length) {
        buffer += decoder.decode(value, { stream: !done });
      }
      if (buffer.includes('\r\n')) {
        buffer = buffer.replace(/\r\n/g, '\n');
      }
      if (done) {
        const trailing = decoder.decode();
        if (trailing) buffer += trailing;
        if (buffer.trim()) {
          buffer = `${buffer}\n\n`;
        }
      }

      let separatorIndex = buffer.indexOf('\n\n');
      while (separatorIndex !== -1) {
        const rawEvent = buffer.slice(0, separatorIndex);
        buffer = buffer.slice(separatorIndex + 2);
        separatorIndex = buffer.indexOf('\n\n');

        if (!rawEvent.trim() || rawEvent.startsWith(':')) {
          continue;
        }

        const { eventName, data } = parseSseEvent(rawEvent);
        const event = (() => {
          if (data && typeof data === 'object' && !Array.isArray(data) && typeof (data as AgUiEvent<TState>).type === 'string') {
            return data as AgUiEvent<TState>;
          }
          return {
            type: eventName || 'CUSTOM',
            ...(data && typeof data === 'object' && !Array.isArray(data)
              ? (data as Record<string, unknown>)
              : { value: data }),
          } as AgUiEvent<TState>;
        })();

        args.onEvent(event);

        if (event.type === 'RUN_FINISHED') {
          terminalType = 'finished';
        } else if (event.type === 'RUN_ERROR') {
          terminalType = 'error';
        }
      }

      if (done) break;
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // The stream may already be closed or cancelled during cleanup.
    }
  }

  return { terminalType };
}
